import { FC, Fragment, ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { ErrorBoundary } from "react-error-boundary";
import type { PluggableList } from "unified";

import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { ExtendedComponents } from "shared/ui/markdown/types";
import { MarkdownComponentLink } from "shared/ui/markdown/components/link";
import { MarkdownComponentImg } from "shared/ui/markdown/components/img";

type MarkdownPreviewPropsI = PropsWithClassNameI & {
  children: null | string | undefined;
  remarkPlugins?: PluggableList;
  rehypePlugins?: PluggableList;
  components?: Partial<ExtendedComponents>;
  hrefBaseUrl?: string;
};

const schema = {
  tagNames: [
    "p",
    "strong",
    "em",
    "ul",
    "ol",
    "li",
    "a",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "hr",
    "img",

    // Custom shortcode elements for Gitbook markdown
    "gitbook-hint",
    "gitbook-embed",

    /**
     * Details and summary are HTML5 semantic tag which allow to make a
     * collapsed section natively
     */
    "details",
    "summary",
  ],
  attributes: {
    details: [],
    summary: [],
    a: ["href", "title"], // Allow basic attributes for links
    img: ["src", "alt", "title"], // Allow basic attributes for images
    "gitbook-hint": ["style"],
    "gitbook-embed": ["url"],
  },
};

/**
 * Markdown preview component that renders markdown content with custom components and sanitization.
 * @param className - The class name to apply to the preview container.
 * @param children - The markdown content to render.
 * @param remarkPlugins - The remark plugins to use (Remark parses markdown into Abstract Syntax Tree. Remark plugins are used to preprocess the AST before being converted to HTML).
 * @param rehypePlugins - The rehype plugins to use (Rehype converts AST to HTML. Rehype plugins are used to postprocess the HTML before it is rendered).
 * @param components - Optional mapping of html tags to custom components.
 * @param hrefBaseUrl - The base URL to use for relative links that are in the markdown.
 */
export const MarkdownPreview: FC<MarkdownPreviewPropsI> = ({
  className,
  children,
  remarkPlugins = [],
  rehypePlugins = [],
  components = {},
  hrefBaseUrl,
}) => (
  <ErrorBoundary
    fallbackRender={() => (
      <div className="b-typography-detail2 text-black/60">
        Error while displaying some content.
      </div>
    )}
  >
    <ReactMarkdown
      className={className}
      remarkPlugins={remarkPlugins}
      rehypePlugins={[
        rehypeRaw, // Parse raw HTML
        [rehypeSanitize, schema], // Sanitize output (Sanitize the HTML to remove disallowed HTML tags and attributes for security reasons).
        ...rehypePlugins, // Optional additional rehype plugins
      ]}
      components={
        {
          a: (props) => (
            <MarkdownComponentLink {...props} hrefBaseUrl={hrefBaseUrl} />
          ),
          img: MarkdownComponentImg,
          details: ({ ...props }) => {
            if (!props.children) {
              return null;
            }

            const children = props.children;

            if (Array.isArray(children)) {
              const summaryIndex = children?.findIndex(
                (child) => child.type === "summary"
              );

              const isSummaryDetected = summaryIndex >= 0;

              const summary = isSummaryDetected ? children[summaryIndex] : null;
              const rest = isSummaryDetected
                ? children.slice(summaryIndex + 1)
                : children;

              return (
                <details {...props}>
                  {summary}

                  {rest.map((value: string | ReactNode, index: number) => {
                    if (typeof value === "string") {
                      return (
                        <Fragment key={index}>
                          <ReactMarkdown
                            rehypePlugins={[
                              rehypeRaw,
                              [rehypeSanitize, schema],
                            ]}
                          >
                            {value}
                          </ReactMarkdown>
                        </Fragment>
                      );
                    }

                    return value;
                  })}
                </details>
              );
            }

            return <details>{children}</details>;
          },
          ...components,
        } as ExtendedComponents
      }
    >
      {children}
    </ReactMarkdown>
  </ErrorBoundary>
);
